import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPlus, TbTrash } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModalLayout from '../../components/layouts/ModalLayout';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import {
  ContributionFrequency,
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
  ContributionPaymentWithTransactionDetails,
  CreateContributionPlanDto,
  JournalEntryLine,
} from '../../../types/bankAccount';

import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import * as bankAccountService from '../../../services/community/bankAccountService';
import Avatar from '../../components/misc/Avatar';
import ProgressBar from '../../components/misc/ProgressBar';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import ContributionPlanFormWizard from './components/ContributionPlanFormWizard';

export default function ContributionPlansPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  const navigate = useNavigate();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [plans, setPlans] = useState<ContributionPlanWithMembersModel[]>([]);
  const [plansWithPayments, setPlansWithPayments] = useState<ContributionPlanWithPayments[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _.chain(_connections.filter((c) => c.type === ConnectionType.MEMBER))
    .groupBy((c) => +c.dashboardMemberId)
    .mapValues((v) => v[0])
    .value();

  useEffect(() => {
    bankAccountService.getAllContributionPlans().then(setPlans);
  }, []);

  useEffect(() => {
    // Fetch detailed payment information for each plan
    const fetchPlansWithPayments = async () => {
      const plansWithPaymentsData = await Promise.all(
        plans.map((plan) =>
          bankAccountService.getContributionPlanWithPayments(plan.contributionPlanId),
        ),
      );
      setPlansWithPayments(plansWithPaymentsData);
    };

    if (plans.length > 0) {
      fetchPlansWithPayments();
    }
  }, [plans]);

  const createContributionPlan = async (plan: CreateContributionPlanDto): Promise<void> => {
    bankAccountService.createContributionPlan(plan).then((createdPlan) => {
      setPlans((prevPlans) => [
        ...prevPlans,
        {
          ...createdPlan,
          appliesTo: plan.appliesToDashboardMemberId.map((id) => ({ dashboardMemberId: id })),
        },
      ]);
      setOpenCreateModal(false);
    });
  };

  const deleteContributionPlan = async (planId: number) => {
    try {
      await bankAccountService.deleteContributionPlan(planId);
      setPlans((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      setPlansWithPayments((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      toast.success(t('success.deleted'));
    } catch (error: any) {
      toast.error(t('error.deletionFailed', { error: error.message }));
    }
  };

  const handleRowClick = (planId: number) => {
    navigate(`/financial/contribution-plans/${planId}`);
  };

  const getPaymentAmount = (payment: ContributionPaymentWithTransactionDetails): number => {
    return payment.journalEntry?.lines?.reduce((sum: number, line: JournalEntryLine) => sum + line.debit, 0) || 0;
  };

  const calculateProgress = (plan: ContributionPlanWithPayments) => {
    const totalAmount = plan.amount;
    const paidAmount = plan.contributionPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    );
    return totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
  };

  const getPaidAmount = (plan: ContributionPlanWithPayments) => {
    return plan.contributionPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    );
  };

  // Filter plans based on search query
  const filteredPlans = useMemo(() => {
    if (!searchQuery.trim()) {
      return plans;
    }

    const query = searchQuery.toLowerCase().trim();
    return plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) || plan.description.toLowerCase().includes(query),
    );
  }, [plans, searchQuery]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {plans.length > 0 && (
          <>
            <SearchBar
              className="max-w-[380px]"
              search={searchQuery}
              setSearch={setSearchQuery}
            />
            <PrimaryButton
              text={t('create')}
              icon={TbPlus}
              onClick={() => setOpenCreateModal(true)}
              className="min-w-[200px]"
            />
          </>
        )}
      </div>

      <div className="flex flex-col gap-8">
        {plans.length === 0 ? (
          <EmptyScreenView
            title={t('noPlans')}
            subtitle={t('createFirstPlan')}
            primaryButton={
              <PrimaryButton
                text={t('createFirstButton')}
                icon={TbPlus}
                onClick={() => setOpenCreateModal(true)}
              />
            }
          />
        ) : (
          <div className="flex flex-col gap-8">
            <FinancialTable
              columns={[
                { id: 'name', label: t('table.name') },
                { id: 'amount', label: t('table.amount') },
                { id: 'installments', label: t('table.installments') },
                { id: 'assignedTo', label: t('table.assignedTo') },
                { id: 'paid', label: t('table.paid') },
                { id: 'actions', label: t('table.actions') },
              ]}
              rows={filteredPlans}
              renderRow={(plan, index) => {
                const planWithPayments = plansWithPayments.find(
                  (p) => p.contributionPlanId === plan.contributionPlanId,
                );
                const progress = planWithPayments ? calculateProgress(planWithPayments) : 0;
                const paidAmount = planWithPayments ? getPaidAmount(planWithPayments) : 0;

                return (
                  <>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <p className="text-sm font-medium">{plan.name}</p>
                      <p className="text-xs text-primary-300">
                        {plan.description.slice(0, 50)}
                        {plan.description.length >= 50 && '...'}
                      </p>
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      {formatEuroCents(plan.amount, i18n.language)}
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      {plan.numberOfInstallments}
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <AvatarList
                        connections={plan.appliesTo.map(
                          ({ dashboardMemberId }) => connections[dashboardMemberId],
                        )}
                      />
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <div className="flex flex-col gap-1">
                        <ProgressBar
                          progress={progress}
                          variant="primary-light"
                          showText={false}
                        />
                        <div className="flex justify-between text-xs">
                          <span>{progress.toFixed(0)}%</span>
                          <span>{formatEuroCents(paidAmount, i18n.language)}</span>
                        </div>
                      </div>
                    </FinancialDataCell>
                    <FinancialDataCell>
                      <ActionMenu>
                        <ActionMenuButton
                          icon={TbTrash}
                          text={t('table.delete')}
                          className="text-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContributionPlan(plan.contributionPlanId);
                          }}
                        />
                      </ActionMenu>
                    </FinancialDataCell>
                  </>
                );
              }}
            />
          </div>
        )}
      </div>

      <CreateContributionPlanModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        onSubmit={createContributionPlan}
      />
    </>
  );
}

function AvatarList({ connections }: { connections: Connection[] }) {
  return (
    <div className="flex items-center gap-[-8px]">
      {connections.slice(0, 2).map((c, index) => (
        <Avatar
          key={`avatar_contribution_${index}_${c?.dashboardMemberId}`}
          className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
          src={c?.picture}
          alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
        />
      ))}
      {connections.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{connections.length - 2}
        </div>
      )}
    </div>
  );
}


type CreateContributionPlanModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (plan: CreateContributionPlanDto) => Promise<void>;
};

function CreateContributionPlanModal({
  open,
  setOpen,
  onSubmit,
}: CreateContributionPlanModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans.createModal' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _connections.filter((c) => c.type === ConnectionType.MEMBER);
  const [contributionPlan, setContributionPlan] = useState<CreateContributionPlanDto>({
    name: '',
    description: '',
    amount: 0,
    frequency: ContributionFrequency.ONCE,
    startDate: new Date(),
    numberOfInstallments: 1,
    appliesToDashboardMemberId: [],
  });
  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl">
      <div className="flex max-h-[calc(100vh-5rem)] flex-col gap-4 overflow-y-auto rounded-md bg-secondary-50 p-6">
        <h2 className="text-lg font-medium">{t('title')}</h2>
        <ContributionPlanFormWizard
          mode="create"
          initialValue={{
            name: '',
            description: '',
            amount: 0,
            frequency: ContributionFrequency.MONTHLY,
            startDate: new Date(),
            numberOfInstallments: 1,
            appliesToDashboardMemberId: [],
          }}
          onSubmit={onSubmit}
        />
      </div>
    </ModalLayout>
  );
}
